import React from 'react';
import { Box, Button, Text, Tooltip } from '@chakra-ui/react';
import { CircleWavyCheck } from 'phosphor-react';
import { IKanbanCard } from '../../../../hooks/useKanban';
import { ILegalizacao, TipoLegalizacao } from '../../../../hooks/useLegalizacao';
import {
  diasAteEntrega,
  formatarCnpj,
  instanteDaApi,
  observacaoDoCard,
  prazoDaEntrega,
  proximaAcao,
  QUEM_META,
  regimeDoProcesso,
  RISCO_META,
  temEtapaAssumida,
  tiposAlteracaoDoProcesso,
  TIPOS_ALT_OPCOES,
  protocoloDoProcesso,
} from '../legalizacaoPlaybook';
import {
  KanbanCardMoldura,
  KanbanCardRodape,
  KanbanCardSelos,
  KanbanCardTitulo,
  KanbanSelo,
} from '../../../../components/kanban/CardPadrao';
import { AnexosDoCard, LinhaDeAcao, ResponsavelDoProcesso } from './CardDoProcesso';
import { FaixaObservacao } from './FaixaObservacao';

interface LegalizacaoCardEntregueProps {
  card: IKanbanCard;
  legalizacao: ILegalizacao;
  tipo: TipoLegalizacao;
  columnTitle: string;
  obsAberta: boolean;
  // eslint-disable-next-line no-unused-vars
  onClick: (card: IKanbanCard, leg: ILegalizacao) => void;
  // eslint-disable-next-line no-unused-vars
  onToggleObs: (cardId: number) => void;
  // eslint-disable-next-line no-unused-vars
  onFinalizar: (card: IKanbanCard, leg: ILegalizacao) => void;
}

/** Verde de "pronto" — o mesmo do `QUEM_META.nenhum`. */
const COR_PRONTO = QUEM_META.nenhum.cor;

/** "05/08" — com o ano só quando não é o corrente. */
const dataCurta = (iso?: string | null): { curta: string; completa: string } | null => {
  const d = instanteDaApi(iso);
  if (!d) return null;
  const fmt = (o: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString('pt-BR', { ...o, timeZone: 'America/Sao_Paulo' });
  const completa = fmt({ day: '2-digit', month: '2-digit', year: 'numeric' });
  const doAno = completa.slice(-4) === String(new Date().getFullYear());
  return {
    curta: fmt(
      doAno
        ? { day: '2-digit', month: '2-digit' }
        : { day: '2-digit', month: '2-digit', year: '2-digit' },
    ),
    completa,
  };
};

/**
 * Card da COLUNA FINAL: resumo do que foi entregue + "Finalizar".
 *
 * Não pinta risco: o semáforo compara o prazo com HOJE, então até o processo entregue no
 * prazo viraria "Atrasado" parado aqui. O prazo aparece como fato da entrega.
 */
export const LegalizacaoCardEntregue: React.FC<LegalizacaoCardEntregueProps> = ({
  card,
  legalizacao: leg,
  tipo,
  columnTitle,
  obsAberta,
  onClick,
  onToggleObs,
  onFinalizar,
}) => {
  const estado = ((leg.estado_etapas as Record<string, Record<string, unknown>> | null)?.[
    columnTitle
  ] ?? {}) as Record<string, unknown>;
  const tiposAlt = tiposAlteracaoDoProcesso(leg.estado_etapas);
  // MESMA função do card comum e do gate do drawer: "pronto" aqui é o checklist da coluna
  // final fechado, nem mais nem menos.
  const acao = proximaAcao(tipo, columnTitle, estado, {
    uf: leg.junta_uf,
    municipio: leg.municipio,
    tiposAlteracao: tiposAlt,
    regime: regimeDoProcesso(leg.estado_etapas),
  });
  const pronto = acao.quem === 'nenhum';

  const entregueEm = dataCurta(card.moved_at);
  // Baixa não entrega nada ao cliente: a última etapa dela é a guarda dos documentos.
  const rotuloDaChegada = tipo === 'baixa' ? 'Concluída' : 'Entregue';
  const prazo = prazoDaEntrega(card.moved_at, card.expira_em);
  // Com etapa assumida o processo começou fora do sistema: a duração seria inventada.
  const duracao = temEtapaAssumida(leg.estado_etapas)
    ? null
    : diasAteEntrega(card.dt_criacao, card.moved_at);
  const obs = observacaoDoCard(leg);
  const mudou =
    tipo === 'alteracao' && tiposAlt?.length
      ? tiposAlt.map(t => TIPOS_ALT_OPCOES.find(o => o.value === t)?.label ?? t).join(', ')
      : null;

  const dados: { rotulo: string; valor: string }[] = [];
  if (leg.cnpj) {
    dados.push({
      rotulo: tipo === 'baixa' ? 'CNPJ baixado' : 'CNPJ',
      valor: formatarCnpj(leg.cnpj) ?? leg.cnpj,
    });
  }
  if (tipo === 'constituicao' && leg.nire) dados.push({ rotulo: 'NIRE', valor: leg.nire });
  // O do início, se não houve novo protocolo (o número não muda depois do protocolo).
  const protocolo = protocoloDoProcesso(leg);
  if (protocolo) {
    dados.push({
      rotulo: 'Protocolo',
      valor: leg.junta_sigla ? `${protocolo} · ${leg.junta_sigla}` : protocolo,
    });
  } else if (leg.junta_sigla) {
    dados.push({ rotulo: 'Junta', valor: leg.junta_sigla });
  }
  if (leg.contrato_societario_id) {
    dados.push({ rotulo: 'Contrato', valor: `#${leg.contrato_societario_id}` });
  }

  let rotuloPrazo: { texto: string; bg: string; cor: string } | null = null;
  if (prazo?.situacao === 'no_prazo') {
    rotuloPrazo = { texto: 'No prazo', bg: '#E8F3EC', cor: COR_PRONTO };
  } else if (prazo?.situacao === 'atrasado') {
    rotuloPrazo = {
      texto: `${prazo.dias} ${prazo.dias === 1 ? 'dia' : 'dias'} após o prazo`,
      bg: RISCO_META[1].seloBg,
      cor: RISCO_META[1].seloCor,
    };
  } else if (prazo?.situacao === 'sem_prazo') {
    rotuloPrazo = { texto: 'Sem prazo', bg: 'gray.100', cor: 'gray.600' };
  }

  return (
    // Mesma moldura do card comum (padrão Estatuto/Eventos). A faixa verde marca o que já
    // pode ser finalizado — é a única ação que resta nesta coluna.
    <KanbanCardMoldura faixa={pronto ? COR_PRONTO : null} onClick={() => onClick(card, leg)}>
      <KanbanCardTitulo>{card.titulo}</KanbanCardTitulo>

      {entregueEm && (
        <Tooltip
          label={`Chegou em "${columnTitle}" em ${entregueEm.completa}${
            duracao !== null ? ' — contando do cadastro do processo' : ''
          }`}
          hasArrow
          openDelay={300}
        >
          <Text
            fontSize="11px"
            color="gray.500"
            mt={0.5}
            w="fit-content"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {rotuloDaChegada} {entregueEm.curta}
            {duracao !== null &&
              ` · ${duracao === 0 ? 'no mesmo dia' : `levou ${duracao} ${duracao === 1 ? 'dia' : 'dias'}`}`}
          </Text>
        </Tooltip>
      )}

      {(rotuloPrazo || leg.rota) && (
        <KanbanCardSelos>
          {rotuloPrazo && (
            <KanbanSelo ponto cor={rotuloPrazo.cor} bg={rotuloPrazo.bg}>
              {rotuloPrazo.texto}
            </KanbanSelo>
          )}
          {leg.rota && <KanbanSelo contorno>Rota {leg.rota}</KanbanSelo>}
        </KanbanCardSelos>
      )}

      {mudou && (
        <Text fontSize="11px" color="gray.600" mt={2} noOfLines={2} lineHeight="1.35">
          <Text as="span" color="gray.400">
            Mudou:{' '}
          </Text>
          {mudou}
        </Text>
      )}

      {dados.length > 0 && (
        <Box mt={2}>
          {dados.map(d => (
            <Text key={d.rotulo} fontSize="11px" color="gray.700" noOfLines={1} lineHeight="1.45">
              <Text as="span" color="gray.400">
                {d.rotulo}:{' '}
              </Text>
              <Text as="span" fontWeight="500" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {d.valor}
              </Text>
            </Text>
          ))}
        </Box>
      )}

      {obs && (
        <Box mt={2.5}>
          <FaixaObservacao
            obs={obs.texto}
            autor={obs.autor}
            aberta={obsAberta}
            onToggle={() => onToggleObs(card.id)}
          />
        </Box>
      )}

      {!pronto && <LinhaDeAcao acao={acao} />}

      <KanbanCardRodape
        esquerda={<ResponsavelDoProcesso nome={leg.responsavel_nome} compacto />}
        direita={
          <>
            <AnexosDoCard qtd={card.anexos_count ?? 0} />
            <Tooltip
              label={
                pronto
                  ? 'Tira do quadro e manda para Finalizados'
                  : 'Complete a etapa para finalizar — clique no card para abrir'
              }
              hasArrow
              openDelay={300}
            >
              {/* O wrapper existe porque botão desabilitado não dispara hover: sem ele o
                  tooltip que explica o bloqueio nunca apareceria. */}
              <Box as="span" display="inline-flex">
                <Button
                  size="xs"
                  h="24px"
                  px={2.5}
                  fontSize="11px"
                  fontWeight="700"
                  variant={pronto ? 'solid' : 'outline'}
                  isDisabled={!pronto}
                  leftIcon={<CircleWavyCheck size={13} weight="bold" />}
                  onClick={e => {
                    e.stopPropagation();
                    onFinalizar(card, leg);
                  }}
                  // Enter no botão não pode abrir o drawer pelo `onKeyDown` do card.
                  onKeyDown={e => e.stopPropagation()}
                >
                  Finalizar
                </Button>
              </Box>
            </Tooltip>
          </>
        }
      />
    </KanbanCardMoldura>
  );
};
